/**
 * Client Profile — fact-find UI helpers for trust/company/partnership structures (form init +
 * validation). The calculation-relevant pieces (entity ownership encoding, apportionment math,
 * CLIENT_STRUCTURE_TYPES data) live server-side only now — see
 * shared/src/calculation/constants/clientStructureTypes.ts. These three stay here because
 * they're pure UI form concerns (new-structure defaults, live validation feedback) with no
 * calculation-engine equivalent.
 */

import type { FactFindStructure } from '../../../shared/src/calculation/factFindTypes.ts';

export type DraftStructure = FactFindStructure;

export function createStructureId(): string {
    return crypto.randomUUID();
}

export function createEmptyStructure(): DraftStructure {
    return {
        id: createStructureId(),
        name: '',
        type: 'family_trust',
        /** When annualDistributionPa set: true (default) = use for pension/care income test; false = cashflow only */
        useDistributionForMeansTest: true,
        notes: '',
    };
}

export interface StructureApportionmentValidation {
    valid: boolean;
    message?: string;
}

export function validateStructureApportionment(
    structure: DraftStructure,
    isPartnered: boolean,
): StructureApportionmentValidation {
    const clientPct = structure.apportionmentClient || 0;
    const partnerPct = isPartnered ? (structure.apportionmentPartner || 0) : 0;
    if (clientPct < 0 || clientPct > 100 || partnerPct < 0 || partnerPct > 100) {
        return { valid: false, message: 'Each percentage must be between 0 and 100.' };
    }
    if (isPartnered && clientPct + partnerPct > 100) {
        return {
            valid: false,
            message: "These percentages can't add up to more than 100%. Check your Centrelink or accountant advice.",
        };
    }
    return { valid: true };
}
